
import { useState, useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
// Extend jsPDF type to include autoTable and lastAutoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => void;
    lastAutoTable?: {
      finalY: number;
    };
  }
}

// Extend Window type to include logoPath
declare global {
  interface Window {
    logoPath?: string;
  }
}

// PDF generation function (from original)
async function generateInvoicePDF(campaignData: any, userdata: any, termss: string, footerEmail: string, footerPhone: string, currencyCode: string, yourLogo: string, showTerms: boolean = true) {
  const jsPDFModule = await import("jspdf");
  const jsPDF = jsPDFModule.default;
  const doc = new jsPDF('p', 'mm', 'a4');
  // Footer for all pages except last
  const addFooter = () => {
    const footerY = pageHeight - 35; // Move footer higher
    doc.setDrawColor(5, 150, 105);
    doc.setLineWidth(3);
    doc.line(15, footerY, pageWidth - 15, footerY);
    doc.setLineWidth(1.5);
    doc.line(15, footerY + 5, pageWidth - 15, footerY + 5);
    // Thank you message
    doc.setFontSize(15);
    doc.setTextColor(5, 150, 105);
    doc.setFont('helvetica', 'bold');
    doc.text('Thank you for choosing Moving Walls!', 15, footerY + 15);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text('Your premium partner for digital out-of-home advertising solutions', 15, footerY + 22);
  };

  // Custom last page footer using main footer config values
  const addLastPageFooter = () => {
    const footerY = pageHeight - 35;
    doc.setDrawColor(5, 150, 105);
    doc.setLineWidth(3);
    doc.line(15, footerY, pageWidth - 15, footerY);
    doc.setLineWidth(1.5);
    doc.line(15, footerY + 5, pageWidth - 15, footerY + 5);
    // Company Name (can be dynamic if needed)
    doc.setFontSize(15);
    doc.setTextColor(5, 150, 105);
    doc.setFont('helvetica', 'bold');
    doc.text('Moving Walls Market', 15, footerY + 14);
    // ABN, website, phone, email
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    // doc.text('ABN: 12 345 678 901', 15, footerY + 22);
    // Use main footer config values for phone and email
    if (window && window.footerConfig) {
      const { email, mobileNumber, website, address } = window.footerConfig;
      if (mobileNumber) {
        doc.text(`Phone: ${mobileNumber}`, 15, footerY + 19);
      }
      if (email) {
        doc.text(`Email: ${email}`, 15, footerY + 24);
      }
      if (website) {
        doc.text(`Website: ${website}`, 15, footerY + 29);
      }
      if (address) {
        doc.text(`Address: ${address}`, 15, footerY + 34);
      }
    } else {
      if (footerPhone) {
        doc.text(`Phone: ${footerPhone}`, 15, footerY + 25);
      }
      if (footerEmail) {
        doc.text(`Email: ${footerEmail}`, 15, footerY + 29);
      }
    }
    // Terms effective (right side)
    doc.setTextColor(148, 163, 184);
    // doc.text('Terms effective: January 2025', pageWidth - 80, footerY + 22);
    // Legally binding (centered)
    doc.setTextColor(100, 116, 139);
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Gradient Header
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, pageWidth, 15, 'F');

  // Header Logo and Text
  doc.setTextColor(255, 255, 255);
  let logoDrawn = false;
  try {
    // Use window.logoPath if available, else fallback to default path
    const logoPath = yourLogo || '';
    // Load image as base64
    const img = new window.Image();
    img.src = logoPath;
    // Wait for image to load
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    // Draw logo (width: 38, height: 18), move higher for top alignment
    doc.addImage(img, 'JPEG', 20, 5, 38, 18);
    logoDrawn = true;
  } catch (e) {
    // Fallback to text if image fails
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Premium Digital Out-of-Home Advertising', 20, 28);

  // Invoice Title
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(5, 150, 105);
  doc.setLineWidth(0.5);
  doc.roundedRect(pageWidth - 80, 5, 75, 25, 3, 3, 'FD');
  doc.setTextColor(5, 150, 105);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const text = 'PAYMENT INVOICE';
  const textWidth = doc.getTextWidth(text);
  const boxWidth = 75;
  const boxX = pageWidth - 80;
  const centeredX = boxX + (boxWidth - textWidth) / 2;
  doc.text(text, centeredX, 19);

  yPosition = 50;

  // Campaign Information Card
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(15, yPosition, 85, 45, 2, 2, 'FD');
  doc.setFillColor(5, 150, 105);
  doc.roundedRect(15, yPosition, 85, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Campaign Information', 18, yPosition + 7);

  doc.setTextColor(51, 65, 85);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const formatCustomDate = (date: Date | string) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };
  const leftContent = [
    `Invoice #: ${campaignData.invoiceId}`,
    `Campaign: ${campaignData.code}`,
    `Name: ${campaignData.name.substring(0, 25)}${campaignData.name.length > 25 ? '...' : ''}`,
    `Start: ${formatCustomDate(campaignData.startDate)}`,
    `End: ${formatCustomDate(campaignData.endDate)}`,
    `Duration: ${campaignData.duration}`
  ];
  leftContent.forEach((line, index) => {
    doc.text(line, 18, yPosition + 15 + (index * 5));
  });

  // Billing Information Card
  const rightCol = 110;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(rightCol, yPosition, 85, 45, 2, 2, 'FD');
  doc.setFillColor(5, 150, 105);
  doc.roundedRect(rightCol, yPosition, 85, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Billing Information', rightCol + 3, yPosition + 7);

  doc.setTextColor(51, 65, 85);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  // Prefer userdata for billing details if available
  const billingDetails = userdata && userdata[0] ? {
    userCompanyName: userdata[0].userCompanyName || 'N/A',
    email: userdata[0].email || 'N/A',
    address: userdata[0].userCompanyAddress || 'N/A',
    city: userdata[0].userCity || 'N/A',
    state: userdata[0].userCompanyState || 'N/A',
    postcode: userdata[0].userCompanyPostcode || 'N/A',
    country: userdata[0].userCountry || 'N/A',
  } : (campaignData.billingDetails || {});
  console.log("print billing details-->", billingDetails);
  console.log("print user data-->", userdata);
  console.log("print campaign data-->", campaignData);

  const rightContent = [
    billingDetails.userCompanyName || 'N/A',
    billingDetails.email || 'N/A',
    billingDetails.address?.substring(0, 30) || 'N/A',
    `${billingDetails.city || 'N/A'}`,
    // billingDetails.postcode || 'N/A',
    billingDetails.country || 'N/A'
  ];
  rightContent.forEach((line, index) => {
    doc.text(line, rightCol + 3, yPosition + 15 + (index * 5));
  });

  yPosition += 60;

  // Inventory Table Header
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Campaign Inventory', 20, yPosition);
  yPosition += 12;

  doc.setFillColor(5, 150, 105);
  doc.roundedRect(15, yPosition - 5, pageWidth - 30, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text('Billboard Name', 20, yPosition + 2, { align: 'left' });
  // Reduce Location column space
  const locationX = 60;
  doc.text('Location', locationX, yPosition + 2, { align: 'left' });
  // Adjust other columns
  const typeX = 100;
  doc.text('Type', typeX, yPosition + 2, { align: 'left' });
  const impressionsHeaderX = 135;
  const priceHeaderX = 170;
  doc.text('Impressions', impressionsHeaderX, yPosition + 2, { align: 'left' });
  doc.text('Price', priceHeaderX, yPosition + 2, { align: 'left' });

  yPosition += 15;

  // Space reserved for footer at the bottom of each page
  const footerSpace = 45;

  // Inventory Items
  campaignData.inventories.forEach((item: any, idx: number) => {
    // If table row would overlap with footer, move to new page
    if (yPosition + 16 + 15 > pageHeight - footerSpace) {
      doc.addPage();
      yPosition = 30;
      doc.setFillColor(5, 150, 105);
      doc.roundedRect(15, yPosition - 5, pageWidth - 30, 12, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text('Billboard Name', 20, yPosition + 3);
      doc.text('Location', 75, yPosition + 3);
      doc.text('Type', 134, yPosition + 3, { align: 'center', maxWidth: 28 });
      doc.text('Impressions', 160, yPosition + 3, { align: 'center', maxWidth: 28 });
      doc.text('Price', pageWidth - 30, yPosition + 3, { align: 'center', maxWidth: 28 });
      yPosition += 15;
    }

    // Table row UI
    const rowHeight = 9;
    const typeBorderHeight = 6;
    const rowSpacing = 1;
    const typeRectY = yPosition - 4 + (rowHeight - typeBorderHeight) / 2;
    const typeTextY = typeRectY + typeBorderHeight / 2;

    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(248, 250, 252); // light gray
      doc.roundedRect(15, yPosition - 4, pageWidth - 30, rowHeight, 3, 3, 'F');
    }

    // Render Type value with colored background and colored text, center aligned
    let typeBgColor = [230, 244, 255]; // Default: light blue
    let typeTextColor = [33, 150, 243]; // Default: blue
    switch ((item.type || '').toLowerCase()) {
      case 'digital':
        typeBgColor = [230, 244, 255]; typeTextColor = [33, 150, 243]; break; // blue
      case 'classic':
        typeBgColor = [232, 245, 233]; typeTextColor = [16, 185, 129]; break; // green
      case 'transit':
        typeBgColor = [255, 243, 224]; typeTextColor = [251, 146, 60]; break; // orange
      case 'package':
        typeBgColor = [243, 229, 245]; typeTextColor = [139, 92, 246]; break; // purple
      default:
        typeBgColor = [238, 238, 238]; typeTextColor = [97, 97, 97]; break; // gray
    }
    // Draw colored rounded rectangle for type background, aligned to header
    doc.setFillColor(typeBgColor[0], typeBgColor[1], typeBgColor[2]);
    doc.roundedRect(typeX, typeTextY - 4, 28, 8, 2, 2, 'F');
    // Draw type text, center aligned, colored, aligned to header
    doc.setTextColor(typeTextColor[0], typeTextColor[1], typeTextColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(item.type, typeX + 3, typeTextY, { align: 'left', maxWidth: 25, baseline: 'middle' });

    // Vertically center name and location, ellipsis for long text
    doc.setTextColor(51, 65, 85);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const maxNameLen = 28;
    const maxLocLen = 28;
    // Billboard Name: wrap to two lines if too long
    const billboardNameMaxWidth = 35; // adjust as needed for column width
    const nameLines = doc.splitTextToSize(item.name, billboardNameMaxWidth);
    if (nameLines.length > 1) {
      doc.text(nameLines[0], 20, typeTextY - 2.5, { align: 'left', baseline: 'middle' });
      doc.text(nameLines[1], 20, typeTextY + 2.5, { align: 'left', baseline: 'middle' });
    } else {
      doc.text(nameLines[0], 20, typeTextY, { align: 'left', baseline: 'middle' });
    }
    // Location remains single line
    let locText = item.location.length > maxLocLen ? item.location.substring(0, maxLocLen - 3) + '...' : item.location;
    doc.text(locText, locationX, typeTextY, { align: 'left', baseline: 'middle' });

    // Type value stays center aligned (already rendered above with colored label)

    // Impressions formatted with comma
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85); // match other text
    doc.setFontSize(10);
    const impressionsText = item.impressions ? item.impressions?.toLocaleString() : '0';
    doc.text(impressionsText, impressionsHeaderX, typeTextY, { align: 'left', maxWidth: 28, baseline: 'middle' });

    // Price: visually consistent with Impressions
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85); // match Impressions color
    doc.setFontSize(10);
    const priceText = item.price ? `${currencyCode || 'N/A'} ${item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${currencyCode || 'N/A'} 0.00`;
    doc.text(priceText, priceHeaderX, typeTextY, { align: 'left', maxWidth: 45, baseline: 'middle' });

    yPosition += rowHeight + rowSpacing;
  });

  // Add Payment Summary below the table
  yPosition += 10; // Add some space after table

  // Payment Summary Card (styled like campaign/billing info)
  const summaryX = 110;
  const summaryWidth = pageWidth - summaryX - 15;
  const summaryHeight = 38;
  const paymentFooterSpace = 45; // Space reserved for footer
  // If payment summary would overlap with footer, move to new page
  if (yPosition + summaryHeight > pageHeight - paymentFooterSpace) {
    doc.addPage();
    yPosition = 30;
  }
  // Card background and grey border (match billing info)
  doc.setFillColor(248, 250, 252); // Light grey inside
  doc.setDrawColor(226, 232, 240); // Grey border
  doc.setLineWidth(0.7);
  doc.roundedRect(summaryX, yPosition, summaryWidth, summaryHeight, 2, 2, 'FD');

  // Emerald header (top of card)
  doc.setFillColor(5, 150, 105);
  doc.roundedRect(summaryX, yPosition, summaryWidth, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Summary', summaryX + 3, yPosition + 7);

  // Subtotal
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  const labelX = summaryX + 3;
  doc.text('Subtotal:', labelX, yPosition + 18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${currencyCode || 'N/A'} ${campaignData.subTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, yPosition + 18, { align: 'right' });

  // GST
  doc.setFont('helvetica', 'normal');
  doc.text(`${campaignData?.taxName} (${Math.round(campaignData.taxPercent)}%):`, labelX, yPosition + 26);
  doc.setFont('helvetica', 'bold');
  doc.text(`${currencyCode || 'N/A'} ${campaignData.gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, yPosition + 26, { align: 'right' });

  // Separator line (start at left edge)
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(summaryX + 3, yPosition + 30, summaryX + summaryWidth - 10, yPosition + 30);

  // Total
  doc.setFontSize(12); // Match other text
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(34, 197, 94); // emerald
  doc.text('TOTAL:', labelX, yPosition + 36);
  doc.text(`${currencyCode || 'N/A'} ${campaignData.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, yPosition + 36, { align: 'right' });

  // Add Terms and Conditions section below payment summary (if enabled)
  if (showTerms) {
  yPosition += summaryHeight + 12; // Space after payment summary

  // Card dimensions
  const termsX = 15;
  const termsWidth = pageWidth - 30;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  const headerText = 'Terms and Conditions';
  const headerTextHeight = doc.getTextDimensions(headerText).h;
  const headerPadding = 6; // Padding above and below text
  const dynamicHeaderHeight = headerTextHeight + headerPadding;

  // Prepare terms content with HTML rendering support
  doc.setFontSize(10); // Ensure font size is set before rendering terms
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  
  // Enhanced HTML to text conversion with formatting preservation
  let formattedContent: Array<{text: string, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean}> = [];
  
  if (typeof window !== 'undefined' && window.document) {
    const tempDiv = window.document.createElement('div');
    tempDiv.innerHTML = termss || '';
    
    // Helper function to extract text with formatting info
    const processElement = (element: Element | Node, inheritBold = false, inheritItalic = false): void => {
      if (element.nodeType === 3) {
        // Text node
        const text = element.textContent?.trim();
        if (text) {
          formattedContent.push({
            text,
            type: 'paragraph',
            bold: inheritBold,
            italic: inheritItalic
          });
        }
        return;
      }
      
      if (element.nodeType !== 1) return;
      
      const el = element as Element;
      const tagName = el.tagName.toLowerCase();
      const textContent = el.textContent?.trim() || '';
      
      // Determine formatting from current element
      const isBold = inheritBold || ['strong', 'b'].includes(tagName);
      const isItalic = inheritItalic || ['em', 'i'].includes(tagName);
      
      switch (tagName) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          if (textContent) {
            const level = Math.min(parseInt(tagName.charAt(1)) || 1, 3);
            formattedContent.push({
              text: textContent,
              type: 'heading',
              level,
              bold: true, // Headings are always bold
              italic: isItalic
            });
          }
          break;
          
        case 'p':
        case 'div':
          if (textContent) {
            // Process child nodes to handle inline formatting
            if (el.children.length > 0 || el.innerHTML.includes('<')) {
              Array.from(el.childNodes).forEach(child => {
                processElement(child, isBold, isItalic);
              });
  } else {
              formattedContent.push({
                text: textContent,
                type: 'paragraph',
                bold: isBold,
                italic: isItalic
              });
            }
          }
          break;
          
        case 'ul':
        case 'ol':
          Array.from(el.children).forEach(li => {
            if (li.tagName.toLowerCase() === 'li') {
              const liText = li.textContent?.trim();
              if (liText) {
                formattedContent.push({
                  text: `• ${liText}`,
                  type: 'list',
                  bold: isBold,
                  italic: isItalic
                });
              }
            }
          });
          break;
          
        case 'li':
          // Handle if li is processed directly
          if (textContent) {
            formattedContent.push({
              text: `• ${textContent}`,
              type: 'list',
              bold: isBold,
              italic: isItalic
            });
          }
          break;
          
        case 'br':
          formattedContent.push({
            text: '',
            type: 'paragraph'
          });
          break;
          
        case 'strong':
        case 'b':
        case 'em':
        case 'i':
        case 'span':
          // Inline formatting - process children with inherited formatting
          Array.from(el.childNodes).forEach(child => {
            processElement(child, isBold, isItalic);
          });
          break;
          
        default:
          // For other elements, extract text content with current formatting
          if (textContent) {
            formattedContent.push({
              text: textContent,
              type: 'paragraph',
              bold: isBold,
              italic: isItalic
            });
          }
          break;
      }
    };
    
    // Process all child nodes of the temp div
    Array.from(tempDiv.childNodes).forEach(node => {
      processElement(node);
    });
    
    // If no content was extracted, fall back to plain text
    if (formattedContent.length === 0) {
      const plainText = tempDiv.textContent || '';
      if (plainText.trim()) {
        formattedContent.push({
          text: plainText.trim(),
          type: 'paragraph'
        });
      }
    }
  } else {
    // Fallback for server-side rendering
    const cleanText = (termss || '').replace(/<[^>]*>/g, '').trim();
    if (cleanText) {
      formattedContent.push({
        text: cleanText,
        type: 'paragraph'
      });
    }
  }
  
  // Split content to fit PDF width and prepare for rendering
  const termsLineHeight = 5;
  const termsParagraphSpacing = 3;
  let allLines: Array<{text: string, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean}> = [];
  
  formattedContent.forEach(item => {
    if (item.text.trim()) {
      const lines = doc.splitTextToSize(item.text, termsWidth - 12);
      lines.forEach((line: string) => {
        if (line.trim()) {
          allLines.push({
            text: line.trim(),
            type: item.type,
            level: item.level,
            bold: item.bold,
            italic: item.italic
          });
        }
      });
      
      // Add spacing after paragraphs and headings
      if (item.type === 'paragraph' || item.type === 'heading') {
        allLines.push({text: '', type: 'paragraph'});
      }
    }
  });
  // Remove last empty line
  if (allLines.length > 0 && allLines[allLines.length - 1].text === '') allLines.pop();
  
  // Check if we need to move the entire terms section to a new page
  const minTermsHeight = dynamicHeaderHeight + (Math.min(5, allLines.length) * termsLineHeight) + 20; // Minimum space needed
  if (yPosition + minTermsHeight > pageHeight - paymentFooterSpace) {
    doc.addPage();
    yPosition = 20;
  }

  // Helper function to draw terms header
  const drawTermsHeader = (startY: number) => {
    // Draw emerald header
    doc.setFillColor(5, 150, 105); // Emerald green
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.roundedRect(termsX, startY, termsWidth, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(headerText, termsX + 4, startY + 7);
  };

  // Helper function to draw card background
  const drawCardBackground = (startY: number, height: number) => {
    doc.setFillColor(248, 250, 252); // Light grey
    doc.setDrawColor(226, 232, 240); // Grey border
    doc.setLineWidth(0.7);
    doc.roundedRect(termsX, startY, termsWidth, height, 2, 2, 'FD');
  };

  // Helper function to set text formatting
  const setTextFormatting = (item: {type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean}) => {
  doc.setTextColor(51, 65, 85);
    
    // Determine font style
    let fontStyle = 'normal';
    if (item.bold && item.italic) {
      fontStyle = 'bolditalic';
    } else if (item.bold) {
      fontStyle = 'bold';
    } else if (item.italic) {
      fontStyle = 'italic';
    }
    
    // Set font size and style based on element type
    switch (item.type) {
      case 'heading':
        if (item.level === 1) {
          doc.setFontSize(14);
        } else if (item.level === 2) {
          doc.setFontSize(12);
        } else {
          doc.setFontSize(11);
        }
        doc.setFont('helvetica', fontStyle === 'normal' ? 'bold' : fontStyle as any);
        break;
      case 'list':
  doc.setFontSize(10);
        doc.setFont('helvetica', fontStyle as any);
        break;
      default: // paragraph
        doc.setFontSize(10);
        doc.setFont('helvetica', fontStyle as any);
        break;
    }
  };

  // Render terms content with page break handling
  let termsY = yPosition + dynamicHeaderHeight + 7;
  let currentPageStartY = yPosition;
  let pageContentLines: Array<{text: string, y: number, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean}> = [];
  
  allLines.forEach((line) => {
    // Calculate line height based on type
    let currentLineHeight = termsLineHeight;
    if (line.type === 'heading') {
      currentLineHeight = line.level === 1 ? 8 : line.level === 2 ? 7 : 6;
    }
    
    // Check if we need a new page (considering footer space)
    if (termsY + currentLineHeight > pageHeight - paymentFooterSpace) {
      // Draw card background for current page
      const currentPageHeight = termsY - currentPageStartY - dynamicHeaderHeight - 7 + 8;
      drawCardBackground(currentPageStartY, dynamicHeaderHeight + currentPageHeight);
      
      // Draw header
      drawTermsHeader(currentPageStartY);
      
      // Render all text content for this page
      pageContentLines.forEach(contentLine => {
        setTextFormatting(contentLine);
        doc.text(contentLine.text, termsX + 6, contentLine.y);
      });
      
      // Add new page and reset
      doc.addPage();
      yPosition = 20;
      currentPageStartY = yPosition;
      termsY = yPosition + dynamicHeaderHeight + 7;
      pageContentLines = [];
    }
    
    if (line.text === '') {
      termsY += termsParagraphSpacing;
    } else {
      // Add extra spacing before headings
      if (line.type === 'heading' && pageContentLines.length > 0) {
        termsY += termsParagraphSpacing;
      }
      
      pageContentLines.push({
        text: line.text,
        y: termsY,
        type: line.type,
        level: line.level,
        bold: line.bold,
        italic: line.italic
      });
      termsY += currentLineHeight;
      
      // Add extra spacing after headings
      if (line.type === 'heading') {
        termsY += termsParagraphSpacing / 2;
      }
    }
  });

  // Draw final page card background and content
  const finalPageHeight = termsY - currentPageStartY - dynamicHeaderHeight - 7 + 8;
  drawCardBackground(currentPageStartY, dynamicHeaderHeight + finalPageHeight);
  
  // Draw final page header
  drawTermsHeader(currentPageStartY);
  
  // Render final page text content
  pageContentLines.forEach(contentLine => {
    setTextFormatting(contentLine);
    doc.text(contentLine.text, termsX + 6, contentLine.y);
  });
  } // End of if (showTerms)

  // Add footer to all pages after content is generated
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i < totalPages; i++) {
    doc.setPage(i);
    addFooter();
  }
  // Last page gets custom footer
  doc.setPage(totalPages);
  addLastPageFooter();
  // Format campaign name for filename (replace spaces with underscores)
  const safeCampaignName = campaignData.name ? campaignData.name.replace(/\s+/g, '_') : '';
  doc.save(`invoice-${campaignData.invoiceId}-${safeCampaignName}.pdf`);
}
import { useQuery } from "@tanstack/react-query";
import { useQuery as useApolloQuery } from '@apollo/client';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Receipt, Calendar, MapPin, DollarSign, CreditCard, CheckCircle, XCircle, Download } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { INVOICE } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';
import { getMediaOwner } from "@/data/media-owners";

// Define interfaces for GraphQL response
interface InvoiceInventory {
  mediaOwnerName: any;
  mediaOwnerId: any;
  id: string;
  inventoryName: string;
  inventoryId: string;
  inventoryType: string;
  inventoryResolutions?: string;
  inventoryPrice?: number;
  inventoryThumbnailUrl?: string;
  inventoryReports?: {
    totalPotentialViews: number;
    totalUniqueReach: number;
    averageFrequency: number;
    cpm: number;
  };
  negotiationSummary?: {
    status: string;
    summaryReport: {
      potentialViews: number;
      uniqueReach: number;
    };
    mediaOwnerPreferredPrice: {
      nowPayPrice: number;
      totalPrice: number;
    };
    buyerPreferredPrice: {
      nowPayPrice: number;
      totalPrice: number;
    };
  };
}

interface InvoiceData {
  id: string;
  dealId: string;
  name: string;
  startDate?: {
    date: string;
    dateStr: string;
    dateFmt: string;
  };
  endDate?: {
    date: string;
    dateStr: string;
    dateFmt: string;
  };
  user?: {
    name: string;
    email: string;
  };
  priceSummary: {
    subTotal: number;
    netTotal: number;
    currency: {
      code: string;
      symbol: string;
      country: string;
    };
    tax: {
      name: string;
      percent: number;
      value: number;
    };
    pcrEnable: boolean;
  };
  inventoriesSummary?: {
    classicInventories: number;
    digitalInventories: number;
    packages: number;
  };
  campaignInventories?: InvoiceInventory[];
  summaryReport?: {
    potentialViews: number;
  };
}

// Import the same campaign data used in campaigns.tsx
const mockInventories = [
  {
    id: 1,
    title: "Perth Central Station",
    location: "Western Australia, Australia",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    price: 5500.00,
    impressions: 811890,
    specifications: "6 x 3m billboard",
    duration: "30 days"
  },
  {
    id: 2,
    title: "Melbourne Cricket Ground",
    location: "Victoria, Australia",
    image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
    price: 8200.00,
    impressions: 1250000,
    specifications: "12 x 3m digital display",
    duration: "15 days"
  },
  // Add more mock inventories as needed
];

// Get campaign data dynamically from the campaigns list
const getCampaignInvoiceData = (campaignId: string) => {
  // This should match the actual campaign data from campaigns.tsx
  const campaignData: { [key: string]: any } = {
    "1": {
      id: 1,
      name: "New campaign 1",
      code: "E - 2025-08070000001",
      invoiceId: "INV-2024-001231",
      startDate: "2025-08-01",
      endDate: "2025-09-01",
      duration: "31 days",
      totalPrice: 5500.00,
      subTotal: 5000.00,
      gst: 500.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 811890,
          price: 5500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "2": {
      id: 2,
      name: "New campaign 2",
      code: "E - 2025-08070000002",
      invoiceId: "INV-2024-001232",
      startDate: "2025-08-05",
      endDate: "2025-09-05",
      duration: "31 days",
      totalPrice: 5500.00,
      subTotal: 5000.00,
      gst: 500.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 2,
          name: "Manhattan Digital Hub",
          location: "New York, NY",
          impressions: 811890,
          price: 5500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "3": {
      id: 3,
      name: "New campaign 3",
      code: "E - 2025-08070000003",
      invoiceId: "INV-2024-001233",
      startDate: "2025-08-10",
      endDate: "2025-09-10",
      duration: "31 days",
      totalPrice: 10500.00,
      subTotal: 9545.45,
      gst: 954.55,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 811890,
          price: 10500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "4": {
      id: 4,
      name: "Samsung S21 ultra",
      code: "E - 2025-08070000004",
      invoiceId: "INV-2024-001234",
      startDate: "2025-08-15",
      endDate: "2025-09-15",
      duration: "31 days",
      totalPrice: 293.33,
      subTotal: 266.67,
      gst: 26.66,
      inventoryCount: 1,
      inventories: [
        {
          id: 2,
          name: "Manhattan Digital Hub",
          location: "New York, NY",
          impressions: 42822,
          price: 293.33,
          resolution: "1440 x 900",
          image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "5": {
      id: 5,
      name: "Testing 123",
      code: "E - 2025-08070000005",
      invoiceId: "INV-2024-001235",
      startDate: "2025-08-20",
      endDate: "2025-09-20",
      duration: "31 days",
      totalPrice: 4950.00,
      subTotal: 4500.00,
      gst: 450.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 733320,
          price: 4950.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "6": {
      id: 6,
      name: "Holiday Sale Campaign",
      code: "E - 2023-06070000006",
      invoiceId: "INV-2024-001236",
      startDate: "2023-12-01",
      endDate: "2023-12-31",
      duration: "31 days",
      totalPrice: 32500.00,
      subTotal: 29545.45,
      gst: 1136.36,
      inventoryCount: 6,
      inventories: [
        {
          id: 1,
          name: "Sydney CBD Premium",
          location: "New South Wales, Australia",
          impressions: 850000,
          price: 2800.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 2,
          name: "Melbourne Central Station",
          location: "Victoria, Australia",
          impressions: 750000,
          price: 2200.00,
          resolution: "1440 x 900",
          image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 3,
          name: "Brisbane Highway Network",
          location: "Queensland, Australia",
          impressions: 420000,
          price: 1800.00,
          resolution: "6x3m",
          image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop",
          type: "Classic"
        },
        {
          id: 4,
          name: "Perth Shopping District",
          location: "Western Australia, Australia",
          impressions: 320000,
          price: 1600.00,
          resolution: "304 x 608",
          image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 5,
          name: "Adelaide Tram Network",
          location: "South Australia, Australia",
          impressions: 280000,
          price: 1400.00,
          resolution: "1200 x 800",
          image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
          type: "Transit"
        },
        {
          id: 6,
          name: "National Highway Package",
          location: "Australia Wide",
          impressions: 1200000,
          price: 2700.00,
          resolution: "Multiple sizes",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Package"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "7": {
      id: 7,
      name: "Spring Fashion Launch",
      code: "E - 2025-09070000007",
      invoiceId: "INV-2024-001237",
      startDate: "2025-09-01",
      endDate: "2025-09-30",
      duration: "30 days",
      totalPrice: 7800.00,
      subTotal: 7090.91,
      gst: 709.09,
      inventoryCount: 3,
      inventories: [
        {
          id: 5,
          name: "Adelaide Tram Network",
          location: "South Australia, Australia",
          impressions: 280000,
          price: 2500.00,
          resolution: "1200 x 800",
          image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
          type: "Transit"
        },
        {
          id: 6,
          name: "National Highway Package",
          location: "Australia Wide",
          impressions: 320000,
          price: 2700.00,
          resolution: "Multiple sizes",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Package"
        },
        {
          id: 7,
          name: "Darwin Waterfront Display",
          location: "Northern Territory, Australia",
          impressions: 290000,
          price: 2600.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "16": {
      id: 16,
      name: "Pending Payment Campaign",
      code: "E - 2023-06070000016",
      invoiceId: "INV-2024-001234",
      startDate: "2024-01-15",
      endDate: "2024-02-15",
      duration: "31 days",
      totalPrice: 7500.00,
      subTotal: 6818.18,
      gst: 681.82,
      inventoryCount: 1,
      inventories: [
        {
          id: 7,
          name: "Melbourne Premium Display",
          location: "Victoria, Australia",
          impressions: 950000,
          price: 7500.00,
          resolution: "1920 x 1200",
          image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    }
  };

  return campaignData[campaignId] || {
    id: parseInt(campaignId) || 0,
    name: "Campaign Payment",
    code: `E - 2023-0607000000${campaignId?.padStart(2, '0') || '00'}`,
    invoiceId: `INV-2024-00123${campaignId || '0'}`,
    startDate: "2024-01-15",
    endDate: "2024-02-15",
    duration: "31 days",
    totalPrice: 8300.00,
    subTotal: 7545.45,
    gst: 754.55,
    inventoryCount: 2,
    inventories: [
      {
        id: 101,
        name: "Standard Digital Display",
        location: "New South Wales, Australia",
        impressions: 650000,
        price: 4500.00,
        resolution: "1920 x 1080",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
        type: "Digital"
      },
      {
        id: 102,
        name: "Metro Station Premium",
        location: "Victoria, Australia",
        impressions: 480000,
        price: 3800.00,
        resolution: "1440 x 900",
        image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
        type: "Digital"
      }
    ],
    billingDetails: {
      companyName: "Test Company Pty Ltd",
      email: "testuser@example.com",
      address: "123 Business Street",
      city: "Sydney",
      state: "NSW",
      postcode: "2000",
      country: "Australia"
    }
  };
};

interface CampaignInvoiceData {
  base_path?: string;
  currencyCode?: string;
  yourLogo?: string;
  configData?: any[];
}

export default function PaymentInvoice({ base_path, currencyCode, yourLogo, configData }: CampaignInvoiceData) {
  // Helper function to check if T&C should be shown
  const getTermsConditionsConfig = () => {
    const termsConfig = configData?.find((page: any) => page.name === "Terms & conditions");
    if (!termsConfig) return { showOnPaymentInvoice: false, showOnDownloadInvoice: true }; // Default values
    
    const configs = termsConfig.configurations || [];
    const paymentInvoiceConfig = configs.find((c: any) => c.key === "payment invoice");
    const downloadInvoiceConfig = configs.find((c: any) => c.key === "download invoice");
    
    return {
      showOnPaymentInvoice: paymentInvoiceConfig?.default ?? false,
      showOnDownloadInvoice: downloadInvoiceConfig?.default ?? true
    };
  };

  const termsConfig = getTermsConditionsConfig();
  
  // Helper to get currency code from response
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const [campaignData, setCampaignData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // const [currencyCode, setCurrencyCode] = useState<string>(localStorage.getItem('currencyCode') || 'AUD');
  const [userdata, setUserdata] = useState<any[]>([]);

  useEffect(() => {
    if (campaignData) {
      console.log('PaymentInvoice campaignData:', campaignData);
    }
  }, [campaignData]);

  const getInvoiceCurrencyCode = () => campaignData.currencyCode || 'N/A';

  // Authentication and environment variables (matching proof-of-play approach)
  const token = localStorage.getItem('authToken');
  const userAccountData = localStorage.getItem("userAccountData");
  const [termss, setTerms] = useState<string>("");
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()};`);
        const data = await response.json();
        const values = data.find((page: any) => page.name === "Terms & conditions");
        const termLen = (values.fields[0].components[0].components).length;
        for (let i = 0; i < termLen; i++) {
          if ('key' in values.fields[0].components[0].components[i]) {
            if (values.fields[0].components[0].components[i].key === "terms_conditionsDocument") {
              if ((values.fields[0].components[0].components[i].defaultValue).length > 0) {
                setTerms(values.fields?.[0]?.components?.[0]?.components?.[i]?.defaultValue?.[0]?.data?.text)
                break;
              }
            } else if (values.fields[0].components[0].components[i].key === "TermsText") {
              setTerms(values.fields[0].components[0].components[i].defaultValue)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching terms content:', error);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    if (userAccountData) {
      const parsedUserAccountData = JSON.parse(userAccountData || "[]");
      console.log("print user account data-->", parsedUserAccountData);
      setUserdata(parsedUserAccountData);

    }
  }, [userAccountData]);

  // Get campaign ID from URL params or query params
  let invoiceId = campaignId;
  if (!invoiceId) {
    const urlParams = new URLSearchParams(window.location.search);
    invoiceId = urlParams.get('campaign') ?? undefined;
  }

  console.log('Payment Invoice Debug:', {
    campaignId,
    invoiceId,
    token: token ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  // Apollo GraphQL query for real data
  const { data: apolloData, loading: apolloLoading, error: apolloError, refetch } = useApolloQuery(INVOICE, {
    variables: {
      accessToken: token,
      campId: invoiceId
    },
    skip: !token || !invoiceId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  // Fallback to mock data for development (only when explicitly enabled)
  const { data: mockInvoiceData, isLoading: mockLoading, error: mockError } = useQuery({
    queryKey: ['/api/payment-invoice', invoiceId],
    queryFn: async () => {
      // Only use mock data when explicitly enabled
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Use existing mock data structure or generate from GraphQL mock
        if (invoiceId) {
          const existingMockData = getCampaignInvoiceData(invoiceId);
          return existingMockData;
        }

        const mockResponse = await generateMockData(INVOICE);
        return mockResponse.data.paymentInvoice || null;
      }

      // Return null if mock data is not enabled
      return null;
    },
    enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true' && !!invoiceId
  });

  // Data processing effects
  useEffect(() => {
    const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
    setIsLoading(apolloLoading || includeMockLoading);
    setError(apolloError ? apolloError.message : (mockError ? (mockError as Error).message : null));
  }, [apolloLoading, mockLoading, apolloError, mockError]);

  // Process invoice data when Apollo data changes or mock data is available
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      // Use mock data
      if (mockInvoiceData) {
        setCampaignData(mockInvoiceData);
      }
    } else {
      // Use real GraphQL data
      if (apolloData?.paymentInvoice) {
        const invoiceData = apolloData.paymentInvoice;

        // Transform GraphQL data to match existing interface
        const transformedData = {
          id: invoiceData.id,
          name: invoiceData.name,
          code: invoiceData.dealId,
          invoiceId: `INV-${invoiceData.id}`,
          startDate: invoiceData.startDate?.dateStr || invoiceData.startDate?.date,
          endDate: invoiceData.endDate?.dateStr || invoiceData.endDate?.date,
          duration: calculateDuration(invoiceData.startDate?.dateStr || invoiceData.startDate?.date, invoiceData.endDate?.dateStr || invoiceData.endDate?.date),
          totalPrice: invoiceData.priceSummary?.netTotal || 0,
          subTotal: invoiceData.priceSummary?.subTotal || 0,
          gst: invoiceData.priceSummary?.tax?.value || 0,
          taxName: invoiceData.priceSummary?.tax?.name || " ",
          taxPercent: invoiceData.priceSummary?.tax?.percent || 0,
          inventoryCount: invoiceData.campaignInventories?.length || 0,
          inventories: transformInventories(invoiceData.campaignInventories || []),
          currencyCode: invoiceData.priceSummary?.currency?.code || 'N/A',
          // billingDetails: {
          //   companyName: userdata[0]?.userCompanyName || "Company Name",
          //   email: userdata[0]?.email || "email@company.com",
          //   address: userdata[0]?.userCompanyAddress || "",
          //   // city: "City",
          //   // state: "State",
          //   // postcode: "0000",
          //   // country: invoiceData.priceSummary?.currency?.country || "Country"
          // }
        };


        setCampaignData(transformedData);
      }
    }
  }, [apolloData, mockInvoiceData]);

  // Monitor currency code changes from localStorage
  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     const newCurrencyCode = localStorage.getItem('currencyCode') || 'AUD';
  //     setCurrencyCode(newCurrencyCode);
  //   };

  //   // Listen for storage changes (when currency is updated in App.tsx)
  //   window.addEventListener('storage', handleStorageChange);

  //   // Also check periodically in case currency is updated in the same tab
  //   const interval = setInterval(() => {
  //     const newCurrencyCode = localStorage.getItem('currencyCode');
  //     if (newCurrencyCode && newCurrencyCode !== currencyCode) {
  //       setCurrencyCode(newCurrencyCode);
  //     }
  //   }, 1000);

  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //     clearInterval(interval);
  //   };
  // }, [currencyCode]);

  // Helper function to calculate duration
  const calculateDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1; // add 1 for inclusive
    return duration > 0 ? `${duration} Day${duration > 1 ? 's' : ''}` : '0 Days';
  };

  // Helper function to transform inventories
  const transformInventories = (inventories: InvoiceInventory[]) => {
    return inventories.map((inventory, index) => ({
      id: index + 1,
      name: inventory.inventoryName,
      location: "Location", // This might need to be added to GraphQL response
      impressions: inventory.inventoryReports?.totalPotentialViews || inventory.negotiationSummary?.summaryReport?.potentialViews || 0,
      price: inventory.inventoryPrice || inventory.negotiationSummary?.buyerPreferredPrice?.totalPrice || 0,
      resolution: inventory.inventoryResolutions || "N/A",
      image: inventory.inventoryThumbnailUrl || "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
      type: mapInventoryType(inventory.inventoryType),
      mediaOwnerId: inventory.mediaOwnerId,
      mediaOwnerName: inventory.mediaOwnerName
    }));
  };

  // Helper function to map inventory types
  const mapInventoryType = (type: string): string => {
    switch (type?.toLowerCase()) {
      case 'digital_billboard':
      case 'digital':
        return 'Digital';
      case 'static_billboard':
      case 'classical_billboard':
      case 'classic':
        return 'Classic';
      case 'package':
        return 'Package';
      case 'transit':
        return 'Transit';
      default:
        return 'Digital';
    }
  };

  const formatCurrency = (amount: number) => {
    const currentCurrencyCode = getInvoiceCurrencyCode();
    const formattedAmount = new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return currentCurrencyCode ? `${currentCurrencyCode} ${formattedAmount}` : formattedAmount;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Invoice...</h1>
            <p className="text-gray-600 mt-2">Please wait while we fetch your invoice data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="hover:bg-emerald-50"
              size="sm"
            >
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back
            </Button>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {error ? 'Error Loading Invoice' : 'Invoice Not Found'}
            </h1>
            <p className="text-gray-600 mt-2">
              {error || 'The requested campaign invoice could not be found.'}
            </p>
            <div className="mt-4 space-x-2">
              <Button
                variant="outline"
                onClick={() => navigate(`${base_path}/my-campaigns`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Campaigns
              </Button>
              {error && (
                <Button
                  onClick={() => {
                    if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && refetch) {
                      refetch();
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50">

      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Back Button at Top */}
          <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-emerald-600 mb-1">Payment Invoice</h1>
            <Button
              variant="outline"
              onClick={() => navigate("/my-campaigns")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-gray-600">View billing details and download invoices</p>

              <p className="text-gray-600 text-sm mt-4">Invoice #{campaignData.invoiceId}</p>
              {/* Debug Info */}
              {import.meta.env.VITE_APP_MOCK_DATA === 'true' && (
                <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                  Using Mock Data - Campaign ID: {invoiceId}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => generateInvoicePDF(
                  campaignData,
                  userdata,
                  termss,
                  userdata[0]?.email || (campaignData.billingDetails?.email ?? ""),
                  userdata[0]?.userCompanyPhone || (campaignData.billingDetails?.phone ?? ""),
                  getInvoiceCurrencyCode(), 
                  yourLogo || "",
                  termsConfig.showOnDownloadInvoice
                )}
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Invoice
              </Button>
              <Badge variant="secondary" className="bg-green-100 text-green-800 px-3 py-1 text-sm">
                <CheckCircle className="h-4 w-4 mr-1" />
                Payment Completed
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Invoice Details - Left Side */}
          <div className="lg:col-span-8 space-y-3">
            {/* Campaign Summary */}
            <Card className="shadow-sm border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg py-2">
                <CardTitle className="flex items-center text-base">
                  <Receipt className="h-4 w-4 mr-2" />
                  Campaign Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campaign Name</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{campaignData.name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Start Date</p>
                      <p className="text-xs font-semibold text-gray-800 flex items-center mt-0.5">
                        <Calendar className="h-3 w-3 mr-1 text-emerald-600" />
                        {formatDate(campaignData.startDate)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campaign ID</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{campaignData.code}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">End Date</p>
                      <p className="text-xs font-semibold text-gray-800 flex items-center mt-0.5">
                        <Calendar className="h-3 w-3 mr-1 text-emerald-600" />
                        {formatDate(campaignData.endDate)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</p>
                  <p className="text-base font-bold text-emerald-600 mt-0.5">{calculateDuration(campaignData.startDate, campaignData.endDate)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Inventory List with Type Segregation */}
            <Card className="shadow-sm border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg py-2">
                <CardTitle className="flex items-center text-base">
                  <MapPin className="h-4 w-4 mr-2" />
                  Inventory Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-4">
                  {campaignData.inventories && campaignData.inventories.length > 0 ? (
                    (() => {
                      const groupedInventories = campaignData.inventories.reduce((acc: any, inventory: any) => {
                        const type = inventory.type || 'Other';
                        if (!acc[type]) acc[type] = [];
                        acc[type].push(inventory);
                        return acc;
                      }, {});

                      const getTypeColor = (type: string) => {
                        switch (type) {
                          case 'Digital': return 'border-l-blue-500 bg-blue-50';
                          case 'Classic': return 'border-l-green-500 bg-green-50';
                          case 'Package': return 'border-l-purple-500 bg-purple-50';
                          case 'Transit': return 'border-l-orange-500 bg-orange-50';
                          default: return 'border-l-gray-500 bg-gray-50';
                        }
                      };

                      const typeTotals = Object.entries(groupedInventories).reduce((acc: any, [type, inventories]: [string, any]) => {
                        acc[type] = inventories.reduce((sum: number, inv: any) => sum + inv.price, 0);
                        return acc;
                      }, {});

                      return Object.entries(groupedInventories).map(([type, inventories]: [string, any]) => (
                        <div key={type} className="space-y-2">
                          <div className={`border-l-4 ${getTypeColor(type)} px-3 py-1.5 rounded-r-md`}>
                            <div className="flex justify-between items-center">
                              <h4 className="text-sm font-semibold text-gray-800">
                                {type} ({inventories.length} {inventories.length !== 1 ? 'Inventories' : 'Inventory'})
                              </h4>
                              <span className="text-sm font-bold text-emerald-600">
                                {formatCurrency(typeTotals[type])}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2 ml-2 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            {inventories.map((inventory: any) => (
                              <div key={inventory.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                                <img
                                  src={inventory.image}
                                  alt={inventory.name}
                                  className="w-16 h-12 object-cover rounded-md shadow-sm"
                                />
                                <div className="flex-1 space-y-1">
                                  <h3 className="text-sm font-bold text-gray-900">{inventory.name}</h3>
                                  <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                    By {inventory.type === 'Package' || inventory.type?.toLowerCase() === 'package'
                                      ? 'Multiple Media Owners'
                                      : (getMediaOwner(inventory.mediaOwnerName)?.name || inventory.mediaOwnerName)}
                                  </span>
                                  <p className="text-xs text-gray-600 flex items-center">
                                    <MapPin className="h-3 w-3 mr-1 text-emerald-600" />
                                    {inventory.location}
                                  </p>
                                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                    <span className="flex items-center">
                                      <span className="w-1 h-1 bg-emerald-500 rounded-full mr-1"></span>
                                      {inventory.impressions?.toLocaleString()} impressions
                                    </span>
                                    <span className="flex items-center">
                                      <span className="w-1 h-1 bg-emerald-500 rounded-full mr-1"></span>
                                      {inventory.resolution}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-emerald-600">{formatCurrency(inventory.price)}</p>
                                  <p className="text-xs text-gray-500">for {campaignData.duration}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-600">
                        {campaignData.inventoryCount} Inventory{campaignData.inventoryCount !== 1 ? 's' : ''} included in this campaign
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Billing Details */}
            <Card className="shadow-sm border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg py-2">
                <CardTitle className="text-base">Billing Details</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1.5 text-sm text-gray-700">
                  {userdata[0]?.userCompanyName && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">Company Name:</span>
                      <span>{userdata[0].userCompanyName}</span>
                    </div>
                  )}
                  {userdata[0]?.email && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">Email:</span>
                      <span>{userdata[0].email}</span>
                    </div>
                  )}
                  {userdata[0]?.userCompanyAddress && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">Address:</span>
                      <span>{userdata[0].userCompanyAddress}</span>
                    </div>
                  )}
                  {userdata[0]?.userCity && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">City:</span>
                      <span>{userdata[0].userCity}</span>
                    </div>
                  )}
                  {userdata[0]?.userCompanyState && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">State:</span>
                      <span>{userdata[0].userCompanyState}</span>
                    </div>
                  )}
                  {userdata[0]?.userCompanyPostcode && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">Postcode:</span>
                      <span>{userdata[0].userCompanyPostcode}</span>
                    </div>
                  )}
                  {userdata[0]?.userCountry && (
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="font-medium">Country:</span>
                      <span>{userdata[0].userCountry}</span>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>
          </div>

          {/* Payment Summary - Right Side */}
          <div className="lg:col-span-4 space-y-3">
            <Card className="shadow-sm border-0 bg-white/90 backdrop-blur-sm sticky top-8">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg py-2">
                <CardTitle className="flex items-center text-base">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                {/* Inventory Type Breakdown */}
                {campaignData.inventories && campaignData.inventories.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700 mb-2">Cost Breakdown by Type:</div>
                    {(() => {
                      const typeTotals = campaignData.inventories.reduce((acc: any, inventory: any) => {
                        const type = inventory.type || 'Other';
                        if (!acc[type]) acc[type] = 0;
                        acc[type] += inventory.price;
                        return acc;
                      }, {});

                      const getTypeBadgeColor = (type: string) => {
                        switch (type) {
                          case 'Digital': return 'bg-blue-100 text-blue-800';
                          case 'Classic': return 'bg-green-100 text-green-800';
                          case 'Package': return 'bg-purple-100 text-purple-800';
                          case 'Transit': return 'bg-orange-100 text-orange-800';
                          case 'Deals': return 'bg-emerald-100 text-emerald-800';
                          default: return 'bg-gray-100 text-gray-800';
                        }
                      };

                      return Object.entries(typeTotals).map(([type, total]: [string, any]) => (
                        <div key={type} className="flex justify-between items-center text-xs">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeColor(type)}`}>
                            {type}
                          </span>
                          <span className="font-semibold text-gray-900">{formatCurrency(total)}</span>
                        </div>
                      ));
                    })()}
                    <Separator className="my-2" />
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">Sub-total</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(campaignData.subTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">{campaignData?.taxName} ({Math.round(campaignData?.taxPercent)}%)</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(campaignData.gst)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-gray-900">Total Paid</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(campaignData.totalPrice)}</span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center text-emerald-800 mb-1.5">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mr-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <span className="text-sm font-bold">Payment Completed</span>
                      <p className="text-xs text-emerald-600 mt-0.5">
                        Payment was processed successfully
                      </p>
                    </div>
                  </div>
                  <div className="text-center text-xs text-emerald-600 mt-2">
                    {/* <p>Transaction completed on {(() => {
                      const date = new Date();
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
                      const year = date.getFullYear();
                      const time = date.toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      return `${day} ${month} ${year}, ${time}`;
                    })()}</p> */}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Section */}
      {termsConfig.showOnPaymentInvoice && (
      <div className="max-w-[95vw] mx-auto px-4 py-8 border-t bg-gray-50/50">
        <Card className="shadow-sm border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-t-lg py-3">
            <CardTitle className="text-lg font-semibold">Terms and Conditions</CardTitle>
          </CardHeader>
          <div className="p-4 terms-content-scroll max-h-[95vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: termss }} />
          {/* <CardContent className="p-6">
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">1. Payment Terms</h4>
                <p>Payment for inventory advertising services is due in full upon booking confirmation. All prices are in {currencyCode || 'AUD'} and include applicable GST where stated. No refunds will be provided once the campaign has commenced.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">2. Campaign Duration & Display</h4>
                <p>Inventory advertising campaigns will run for the specified duration as outlined in this invoice. Campaign start and end dates are fixed and cannot be modified once payment is processed. Display times may vary based on inventory location and local regulations.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">3. Creative Content Requirements</h4>
                <p>All advertising content must be submitted at least 48 hours prior to campaign start date. Content must comply with local advertising standards and regulations. InventoryHub reserves the right to reject content that does not meet technical specifications or community standards.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">4. Liability & Performance</h4>
                <p>While we strive for maximum inventory uptime, InvetoryHub cannot guarantee 100% display availability due to technical issues, weather conditions, or maintenance requirements. Impression counts are estimates based on location traffic data and are not guaranteed figures.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">5. Cancellation Policy</h4>
                <p>Campaign cancellations must be requested at least 72 hours before the scheduled start date to be eligible for a full refund. Cancellations made within 72 hours of campaign start are subject to a 50% cancellation fee. No refunds are available once campaigns have commenced.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">6. Intellectual Property</h4>
                <p>Client warrants that they own or have proper licensing for all creative content submitted. Client indemnifies InventoryHub against any copyright or trademark infringement claims related to displayed content.</p>
              </div>
              
              <div className="border-t pt-4 mt-6">
                <p className="text-xs text-gray-500">
                  <strong>Contact Information:</strong> For questions regarding this invoice or campaign, please contact our support team at <span className="text-emerald-600 font-medium">support@inventoryhub.com</span> or call <span className="text-emerald-600 font-medium">1300 Inventory</span>
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  <strong>InventoryHub Pty Ltd</strong> | ABN: 12 345 678 901 | Level 10, 123 Business Street, Sydney NSW 2000, Australia
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  This document constitutes a legal agreement. By proceeding with payment, you acknowledge acceptance of these terms and conditions.
                </p>
              </div>
            </div>
          </CardContent> */}
        </Card>
      </div>
      )}
    </div>
  );
}